/**
 * CRM Sidebar — left navigation for the CRM shell.
 *
 * Desktop (lg+): a sticky column below the h-12 app bar with a footer holding
 * Help + the collapse toggle (w-60 labels <-> w-16 icon rail; persisted via
 * getPref/setPref('sidebar')). When the user has never chosen, the rail is the
 * default on laptop-width screens (<2xl) so content gets the width.
 * Mobile (<lg): hidden until `mobileOpen`, then a fixed slide-over drawer with
 * full labels; closes on overlay click, Escape, or any nav click.
 *
 * IA: the daily-driver views render first (Today/Calendar, then the Leads and
 * Comms groups, then Reports), and every config surface lives behind the single
 * Settings item (the /crm/settings hub carries its own sub-nav — see
 * SettingsLayout). The shell remounts per route, so the collapse state comes
 * from localStorage on each mount.
 *
 * Badge counts come in via the `counts` prop — CRMLayout fetches them once
 * (see useNavCounts) and shares them with the mobile bottom tab bar, so the
 * two nav surfaces never double-poll the stats endpoints. A null count simply
 * renders no badge.
 *
 * Badges are primary-tinted except one. Settings carries an `alert` badge for
 * referral name mismatches (review §C14): those are not a backlog to work
 * through at leisure, they are a patient who was told someone would call, whose
 * escrowed details expire in 14 days, and whose referral produced no contact,
 * no PDF and no consent row. Red is the difference between "there is work here"
 * and "someone is waiting". Do not add a second alert badge without the same
 * kind of justification — a nav where several things are red has no red left.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  Database,
  Hourglass,
  Inbox,
  LifeBuoy,
  MessagesSquare,
  Microscope,
  Phone,
  Printer,
  SearchCheck,
  Settings,
  Sun,
  X,
  Bot,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getPref, setPref } from '../lib';
import type { NavCounts } from '../useNavCounts';
import CrmMark from './CrmMark';
import { CountBadge, Z } from './ui';

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
  /** Shared nav badge counts (fetched once by CRMLayout via useNavCounts). */
  counts: NavCounts;
}

interface NavItem {
  to: string;
  end?: boolean;
  icon: LucideIcon;
  label: string;
  /** Badge count (undefined/null/0 -> no badge). */
  badge?: number | null;
  /** 'alert' = red badge: someone is waiting on us. See the file header. */
  tone?: 'primary' | 'alert';
  /** Appended to the accessible name so a screen reader gets the urgency too. */
  badgeLabel?: string;
}

function NavRow({
  item,
  collapsed,
  inDrawer,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  /** Rendered inside the mobile drawer (exists up to lg) — keep 44px rows until lg. */
  inDrawer?: boolean;
  onNavigate?: () => void;
}) {
  const { to, end, icon: Icon, label, badge, tone, badgeLabel } = item;
  const showBadge = typeof badge === 'number' && badge > 0;
  const alert = tone === 'alert';
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      title={collapsed ? [label, showBadge ? badgeLabel || `${badge}` : ''].filter(Boolean).join(' — ') : undefined}
      aria-label={showBadge ? `${label} (${badgeLabel || badge})` : label}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
          inDrawer ? 'min-h-[44px] lg:min-h-0 lg:py-1.5' : 'min-h-[44px] sm:min-h-0 sm:py-1.5'
        } focus:outline-none focus:ring-2 focus:ring-primary-500 ${
          collapsed ? 'justify-center px-2' : 'px-3'
        } ${
          isActive
            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700/60 dark:hover:text-gray-100'
        }`
      }
    >
      <span className="relative flex-shrink-0">
        <Icon className="h-4 w-4" />
        {/* Collapsed mode: the count becomes a corner dot on the icon */}
        {collapsed && showBadge && (
          <span
            className={`absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full ring-2 ring-white dark:ring-gray-800 ${
              alert ? 'bg-red-600' : 'bg-primary-600'
            }`}
            aria-hidden="true"
          />
        )}
      </span>
      {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
      {!collapsed && showBadge && (
        <CountBadge count={badge} tone={alert ? 'alert' : 'primary'} className="ml-auto flex-shrink-0" />
      )}
    </NavLink>
  );
}

export default function Sidebar({ mobileOpen, onMobileClose, counts }: SidebarProps) {
  // Explicit user choice wins; otherwise default to the icon rail on
  // laptop-width screens (below 2xl) and to full labels on large desktops.
  const [collapsed, setCollapsed] = useState(() => {
    const stored = getPref('sidebar', '');
    if (stored === 'collapsed') return true;
    if (stored === 'expanded') return false;
    try {
      return !window.matchMedia('(min-width: 1536px)').matches;
    } catch {
      return false;
    }
  });
  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      setPref('sidebar', next ? 'collapsed' : 'expanded');
      return next;
    });
  }, []);

  // Keep the width-based default live across window resizes — but only until
  // the user makes an explicit choice.
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(min-width: 1536px)');
    } catch {
      return;
    }
    const apply = () => {
      if (getPref('sidebar', '') === '') setCollapsed(!mq.matches);
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
  }, []);

  // Close the mobile drawer on Escape while it is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, onMobileClose]);

  // Drawer focus management: on open, lock body scroll and move focus to the
  // close button; on close (or unmount while open), restore both the previous
  // body overflow and focus to whatever element opened the drawer.
  const panelRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!mobileOpen) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus();
    };
  }, [mobileOpen]);

  // Minimal Tab loop-trap: keep Tab / Shift+Tab cycling inside the drawer panel.
  const onDrawerKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !panel.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last || !panel.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  };

  // Everyday views, grouped. A header renders as a muted label when expanded;
  // section boundaries are thin dividers in both modes.
  const sections: { header?: string; items: NavItem[] }[] = [
    {
      items: [
        { to: '/crm', end: true, icon: Sun, label: 'Today', badge: counts.today },
        { to: '/crm/calendar', icon: CalendarDays, label: 'Calendar' },
      ],
    },
    {
      header: 'Leads',
      items: [
        { to: '/crm/inbox', icon: Inbox, label: 'Lead Inbox' },
        {
          to: '/crm/active',
          icon: MessagesSquare,
          label: 'Conversations',
          badge: counts.conversations?.needs_reply,
          badgeLabel: counts.conversations?.needs_reply
            ? `${counts.conversations.needs_reply} awaiting your reply`
            : undefined,
        },
        {
          to: '/crm/awaiting',
          icon: Hourglass,
          label: 'No reply yet',
          badge: counts.conversations?.awaiting,
          badgeLabel: counts.conversations?.awaiting
            ? `${counts.conversations.awaiting} contacted, no reply yet`
            : undefined,
        },
        { to: '/crm/by-study', icon: Microscope, label: 'By study' },
        // Safety net: leads left behind (coverage-invariant catches, network-
        // wide). The badge is the deduped LEAD count, sliced from the worklist
        // counts response like agentReview — primary tone by design (the red
        // budget is spent; see the file header).
        {
          to: '/crm/safety-net',
          icon: LifeBuoy,
          label: 'Safety net',
          badge: counts.safetyNet,
          badgeLabel: counts.safetyNet
            ? `${counts.safetyNet} lead${counts.safetyNet === 1 ? '' : 's'} left behind — no next step scheduled`
            : undefined,
        },
        { to: '/crm/database', icon: Database, label: 'Patients' },
        // Cohort builder: funnel searches over the whole database, saved lists.
        { to: '/crm/search', icon: SearchCheck, label: 'Search' },
        // Texting-agent console: every agent conversation, draft, escalation.
        // The badge is the agent-review backlog — the same `agent_review` bucket
        // Today's own section counts, so the two can never disagree.
        {
          to: '/crm/agent',
          icon: Bot,
          label: 'Agent console',
          badge: counts.agentReview,
          badgeLabel: counts.agentReview
            ? `${counts.agentReview} agent review${counts.agentReview === 1 ? '' : 's'} waiting — escalated questions & eligibility decisions`
            : undefined,
        },
      ],
    },
    {
      header: 'Comms',
      items: [
        { to: '/crm/phone', icon: Phone, label: 'Phone', badge: counts.voicemail },
        // Fax hub: the unmatched-triage queue, history search, standalone send,
        // and the provider-office directory. Open to all CRM users (recruiters
        // work the fax queue); the badge is the unmatched-inbound count.
        { to: '/crm/fax', icon: Printer, label: 'Fax', badge: counts.fax },
      ],
    },
    {
      items: [{ to: '/crm/reports', icon: BarChart3, label: 'Reports' }],
    },
    // One entry for every config surface — the /crm/settings hub carries its
    // own role-gated sub-nav (studies/forms/flows are open to all CRM users,
    // so the item itself is unconditional). The badge is the ONE thing behind
    // Settings that is time-critical rather than configuration: referral name
    // mismatches, which are null for anyone the API would 403 anyway.
    {
      items: [{
        to: '/crm/settings',
        icon: Settings,
        label: 'Settings',
        badge: counts.portalCollisions?.total ?? null,
        tone: 'alert' as const,
        badgeLabel: counts.portalCollisions?.total
          ? `${counts.portalCollisions.total} referral name mismatch${counts.portalCollisions.total === 1 ? '' : 'es'} to call`
          : undefined,
      }],
    },
  ];

  // In-app handbooks — visible to ALL CRM users (the page itself only uses
  // the admin role to pick a default tab; both handbooks are readable by all).
  const helpItem: NavItem = { to: '/crm/help', icon: BookOpen, label: 'Help' };

  const dividerCls = 'mt-3 border-t border-gray-100 pt-3 dark:border-gray-800';
  const groupLabelCls =
    'mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500';

  const renderNav = (collapsedMode: boolean, inDrawer?: boolean, onNavigate?: () => void) => (
    <>
      {sections.map((section, idx) => (
        <div key={section.header || `s${idx}`} className={idx > 0 ? dividerCls : undefined}>
          {section.header && !collapsedMode && <div className={groupLabelCls}>{section.header}</div>}
          <div className="space-y-0.5">
            {section.items.map((item) => (
              <NavRow key={item.to} item={item} collapsed={collapsedMode} inDrawer={inDrawer} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <>
      {/* ---- Desktop: sticky column below the h-12 app bar, collapsible ---- */}
      <aside
        className={`hidden flex-shrink-0 self-stretch border-r border-gray-200 bg-white transition-[width] duration-150 dark:border-gray-700 dark:bg-gray-800 lg:flex lg:flex-col ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        <div className="sticky top-12 flex h-[calc(100vh-3rem)] w-full flex-col">
          <nav aria-label="CRM navigation" className="flex-1 overflow-y-auto p-3">
            {renderNav(collapsed)}
          </nav>
          <div className="border-t border-gray-200 p-2 dark:border-gray-700">
            {collapsed ? (
              <div className="space-y-0.5">
                <NavRow item={helpItem} collapsed />
                <button
                  type="button"
                  onClick={toggleCollapsed}
                  aria-label="Expand sidebar"
                  title="Expand sidebar"
                  className="flex w-full items-center justify-center rounded-lg px-2 py-1.5 text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200"
                >
                  <ChevronsRight className="h-4 w-4 flex-shrink-0" />
                </button>
              </div>
            ) : (
              <>
                <div className="mt-0.5 flex items-center gap-1">
                  <div className="min-w-0 flex-1">
                    <NavRow item={helpItem} collapsed={false} />
                  </div>
                  <button
                    type="button"
                    onClick={toggleCollapsed}
                    aria-label="Collapse sidebar"
                    title="Collapse sidebar"
                    className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ---- Mobile: slide-over drawer ---- */}
      {mobileOpen && (
        <div className={`fixed inset-0 ${Z.modal} lg:hidden`}>
          <div className="absolute inset-0 bg-black/50" onClick={onMobileClose} aria-hidden="true" />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="CRM navigation"
            onKeyDown={onDrawerKeyDown}
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-white shadow-xl dark:bg-gray-800"
          >
            <div className="flex items-center justify-between border-b border-gray-200 py-2 pl-4 pr-2 dark:border-gray-700">
              <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-600">
                  <CrmMark className="h-3.5 w-3.5 text-white" />
                </span>
                Lead CRM
              </span>
              <button
                ref={closeBtnRef}
                type="button"
                onClick={onMobileClose}
                aria-label="Close navigation"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0 sm:p-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav aria-label="CRM navigation" className="flex-1 overflow-y-auto p-3">
              {renderNav(false, true, onMobileClose)}
              <div className={dividerCls}>
                <NavRow item={helpItem} collapsed={false} inDrawer onNavigate={onMobileClose} />
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
